/**
 * localDbService.ts
 * High-performance, unlimited-capacity, IndexedDB-backed local storage manager.
 * Keeps an in-memory synchronous cache updated in real-time to allow synchronous 
 * state reads during React rendering and event handling.
 */

const DB_NAME = 'nutrition_app_db';
const STORE_NAME = 'key_value_store';
const DB_VERSION = 1;

class LocalDbService {
  private db: IDBDatabase | null = null;
  private cache = new Map<string, string>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  private safeGetItem(key: string): string | null {
    let val: string | null = null;
    if (typeof window !== 'undefined' && (window as any).__originalGetItem) {
      val = (window as any).__originalGetItem(key);
    } else if (typeof localStorage !== 'undefined') {
      val = localStorage.getItem(key);
    }
    if (val === "undefined" || val === "null") return null;
    return val;
  }

  private safeSetItem(key: string, value: string): void {
    if (typeof window !== 'undefined' && (window as any).__originalSetItem) {
      (window as any).__originalSetItem(key, value);
    } else if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  private safeRemoveItem(key: string): void {
    if (typeof window !== 'undefined' && (window as any).__originalRemoveItem) {
      (window as any).__originalRemoveItem(key);
    } else if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }

  private safeClear(): void {
    if (typeof window !== 'undefined' && (window as any).__originalClear) {
      (window as any).__originalClear();
    } else if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
  }

  private isReconnecting = false;

  private handleDbError(err: any): void {
    const errMsg = err?.message || String(err);
    const isConnectionError = errMsg.includes('closing') || 
                              errMsg.includes('closed') || 
                              errMsg.includes('connection') || 
                              err?.name === 'InvalidStateError';
    
    if (!isConnectionError) {
      console.warn('[LocalDB] Non-connection database transaction/operation error (ignored):', err);
      return;
    }

    console.warn('[LocalDB] DB connection/transaction error detected, initiating recovery:', err);
    
    if (this.db) {
      try {
        this.db.close();
      } catch (e) {}
      this.db = null;
    }
    this.initialized = false;

    if (!this.isReconnecting) {
      this.isReconnecting = true;
      setTimeout(async () => {
        try {
          console.log('[LocalDB] Attempting automatic DB connection recovery...');
          await this.init();
        } catch (reconnectErr) {
          console.error('[LocalDB] Database reconnection failed:', reconnectErr);
        } finally {
          this.isReconnecting = false;
        }
      }, 1000);
    }
  }

  /**
   * Initializes the IndexedDB database and loads all keys into the in-memory cache.
   * Also migrates heavy keys from localStorage on the first run.
   */
  public async init(): Promise<void> {
    if (this.initialized && this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        };

        request.onsuccess = async (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          this.db = db;

          db.onversionchange = () => {
            console.warn('[LocalDB] Database version change detected. Closing database connection.');
            try {
              db.close();
            } catch (e) {}
            if (this.db === db) {
              this.db = null;
              this.initialized = false;
            }
          };

          db.onclose = () => {
            console.warn('[LocalDB] Database connection closed.');
            if (this.db === db) {
              this.db = null;
              this.initialized = false;
            }
          };

          try {
            await this.loadAllToCache();
            this.initialized = true;
            console.log('[LocalDB] Successfully initialized and synced with memory cache.');
            resolve();

            // Run migration and archive restoration non-blockingly in background
            this.migrateFromLocalStorage().catch(e => console.warn('[LocalDB] Background migration note:', e));
            this.restoreAllArchivedMeals().catch(e => console.warn('[LocalDB] Background restore note:', e));
          } catch (err) {
            console.error('[LocalDB] Failed to load store:', err);
            resolve(); // Resolve anyway to allow application to boot
          }
        };

        request.onerror = (event) => {
          console.error('[LocalDB] Database open error:', request.error);
          reject(request.error);
        };
      } catch (err) {
        console.error('[LocalDB] Sync error during indexedDB.open:', err);
        resolve();
      }
    });

    try {
      const timeoutPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          console.warn('[LocalDB] Init timeout (4s) reached. Proceeding with fallback.');
          this.initialized = true;
          resolve();
        }, 4000);
      });
      await Promise.race([this.initPromise, timeoutPromise]);
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Loads all key-value entries from IndexedDB into the in-memory cache.
   */
  private async loadAllToCache(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      try {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.openCursor();

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
          if (cursor) {
            this.cache.set(cursor.key as string, cursor.value as string);
            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => {
          reject(request.error);
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Migrates heavy or all items from localStorage to IndexedDB on the first run.
   */
  private async migrateFromLocalStorage(): Promise<void> {
    if (typeof localStorage === 'undefined') return;

    const migrationFlag = 'indexeddb_migrated_v1';
    if (this.safeGetItem(migrationFlag) === 'true') {
      return;
    }

    console.log('[LocalDB] First-time run detected. Migrating local storage keys to IndexedDB...');

    const keysToMigrate = [
      'meal_history',
      'chat_history',
      'scanned_products',
      'water_logs',
      'auth_logs',
      'auth_error_logs',
      'main_nutrition_offline_queue',
      'favorites',
      'user_goals',
      'userName',
      'app_vibe',
      'onboarding_completed',
      'app_lang',
      'theme'
    ];

    // Migrate all keys present in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key !== migrationFlag) {
        const value = this.safeGetItem(key);
        if (value !== null) {
          await this.setItem(key, value);
        }
      }
    }

    // Set migration completed flag in localStorage
    this.safeSetItem(migrationFlag, 'true');

    // Remove heavy objects from localStorage to free up browser storage quota immediately
    const heavyKeysToPrune = [
      'meal_history',
      'chat_history',
      'scanned_products',
      'water_logs',
      'auth_logs',
      'auth_error_logs',
      'main_nutrition_offline_queue',
      'favorites'
    ];

    heavyKeysToPrune.forEach((key) => {
      this.safeRemoveItem(key);
    });

    // Prune dynamic pending sync items from localStorage too
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('pending_sync_') || key.startsWith('pending_water_sync_'))) {
        this.safeRemoveItem(key);
      }
    }

    console.log('[LocalDB] Migration completed. Heavy keys cleared from localStorage.');
  }

  /**
   * Synchronously gets an item from the in-memory cache.
   * This is a direct drop-in replacement for localStorage.getItem().
   */
  public getItemSync(key: string): string | null {
    const cached = this.cache.get(key);
    if (cached !== undefined) {
      if (cached === "undefined" || cached === "null") return null;
      return cached;
    }
    // Fallback to localStorage if not found and migration flag hasn't been processed yet
    const val = this.safeGetItem(key);
    if (val === "undefined" || val === "null") return null;
    return val;
  }

  /**
   * Asynchronously gets an item from the database.
   */
  public async getItem(key: string): Promise<string | null> {
    const cached = this.cache.get(key);
    if (cached !== undefined) {
      if (cached === "undefined" || cached === "null") return null;
      return cached;
    }

    if (!this.db) {
      const val = this.safeGetItem(key);
      if (val === "undefined" || val === "null") return null;
      return val;
    }

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          let val = request.result as string | undefined;
          if (val === "undefined" || val === "null") val = undefined;
          if (val !== undefined) {
            this.cache.set(key, val);
            resolve(val);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          resolve(null);
        };
      } catch (err) {
        this.handleDbError(err);
        const val = this.safeGetItem(key);
        resolve(val === "undefined" || val === "null" ? null : val);
      }
    });
  }

  /**
   * Stores a key-value pair in IndexedDB asynchronously, and updates the cache synchronously.
   * Direct replacement for localStorage.setItem().
   */
  public async setItem(key: string, value: string): Promise<void> {
    const criticalKeys = ['meal_history', 'water_logs', 'scanned_products', 'favorites', 'user_goals'];
    if (!this.initialized && criticalKeys.includes(key) && (!value || value === '[]' || value === '{}')) {
      console.warn(`[LocalDB Safety] Blocked writing empty/uninitialized value to "${key}" because LocalDB is not initialized yet. This prevents accidental data loss!`);
      return;
    }

    this.cache.set(key, value);

    // Also write to localStorage for extremely lightweight, non-heavy keys as secondary fallback
    const lightWeightKeys = [
      'userName',
      'app_vibe',
      'onboarding_completed',
      'app_lang',
      'theme',
      'app_illumination',
      'app_illumination_color',
      'google_fit_connected',
      'apple_health_connected',
      'indexeddb_migrated_v1'
    ];
    if (lightWeightKeys.includes(key)) {
      try {
        this.safeSetItem(key, value);
      } catch (err) {
        // ignore quota exceeded for light keys
      }
    }

    if (!this.db) {
      return;
    }

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(value, key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error(`[LocalDB] Error writing key "${key}":`, request.error);
          resolve();
        };
      } catch (err) {
        this.handleDbError(err);
        resolve();
      }
    });
  }

  /**
   * Removes an item from the database asynchronously, and removes it from the cache synchronously.
   */
  public async removeItem(key: string): Promise<void> {
    this.cache.delete(key);
    this.safeRemoveItem(key);

    if (!this.db) {
      return;
    }

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          resolve();
        };
      } catch (err) {
        this.handleDbError(err);
        resolve();
      }
    });
  }

  /**
   * Clears all items in both IndexedDB and the cache.
   */
  public async clear(): Promise<void> {
    this.cache.clear();
    this.safeClear();

    if (!this.db) {
      return;
    }

    return new Promise((resolve) => {
      try {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          resolve();
        };
      } catch (err) {
        this.handleDbError(err);
        resolve();
      }
    });
  }

  /**
   * Helper to scan all keys matching a specific prefix from cache.
   */
  public scanKeysWithPrefixSync(prefix: string): { key: string; value: string }[] {
    const results: { key: string; value: string }[] = [];
    this.cache.forEach((value, key) => {
      if (key.startsWith(prefix)) {
        results.push({ key, value });
      }
    });
    return results;
  }

  /**
   * Scans and automatically archives meals older than 6 months.
   * Keeps primary history light and fast.
   */
  public async archiveOldMeals(): Promise<{ archivedCount: number; activeCount: number }> {
    try {
      const savedHistory = await this.getItem('meal_history');
      if (!savedHistory) {
        return { archivedCount: 0, activeCount: 0 };
      }

      let meals: any[] = [];
      try {
        meals = JSON.parse(savedHistory);
      } catch (err) {
        console.warn('[LocalDB Archive] Failed to parse meal history:', err);
        return { archivedCount: 0, activeCount: 0 };
      }

      if (!Array.isArray(meals) || meals.length === 0) {
        return { archivedCount: 0, activeCount: 0 };
      }

      const sixMonthsAgo = Date.now() - (6 * 30 * 24 * 60 * 60 * 1000);
      const activeMeals: any[] = [];
      const oldMeals: any[] = [];

      meals.forEach((meal) => {
        // Enforce milliseconds timestamp
        const ts = meal.timestamp ? (meal.timestamp < 99999999999 ? meal.timestamp * 1000 : meal.timestamp) : Date.now();
        if (ts < sixMonthsAgo) {
          oldMeals.push(meal);
        } else {
          activeMeals.push(meal);
        }
      });

      if (oldMeals.length === 0) {
        return { archivedCount: 0, activeCount: meals.length };
      }

      console.log(`[LocalDB Archive] Found ${oldMeals.length} meals older than 6 months. Archiving...`);

      // Load existing archive
      const existingArchiveCompressed = await this.getItem('meal_history_archive');
      let archivedMeals: any[] = [];

      if (existingArchiveCompressed) {
        try {
          const decompressed = await decompressString(existingArchiveCompressed);
          const parsedArchive = JSON.parse(decompressed);
          if (Array.isArray(parsedArchive)) {
            archivedMeals = parsedArchive;
          }
        } catch (err) {
          console.error('[LocalDB Archive] Failed to load/decompress existing archive, recreating:', err);
        }
      }

      // Merge old meals into archived collection (avoiding duplicates)
      const archiveMap = new Map<string, any>();
      archivedMeals.forEach(m => {
        if (m.id) archiveMap.set(m.id, m);
      });
      oldMeals.forEach(m => {
        if (m.id) archiveMap.set(m.id, m);
      });

      const updatedArchive = Array.from(archiveMap.values());
      const updatedArchiveJson = JSON.stringify(updatedArchive);
      
      // Compress the merged archive
      const compressedArchive = await compressString(updatedArchiveJson);

      // Save both collections without truncating active history
      await this.setItem('meal_history_archive', compressedArchive);
      await this.setItem('meal_history', JSON.stringify(meals));

      console.log(`[LocalDB Archive] Successfully archived ${oldMeals.length} meals. Active: ${activeMeals.length}. Archive total: ${updatedArchive.length}`);

      return {
        archivedCount: oldMeals.length,
        activeCount: activeMeals.length
      };
    } catch (e) {
      console.error('[LocalDB Archive] Error performing automatic cleanup:', e);
      return { archivedCount: 0, activeCount: 0 };
    }
  }

  /**
   * Retrieves all archived meals, decompressing them automatically.
   */
  public async getArchivedMeals(): Promise<any[]> {
    try {
      const compressed = await this.getItem('meal_history_archive');
      if (!compressed) return [];
      const decompressed = await decompressString(compressed);
      const parsed = JSON.parse(decompressed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('[LocalDB Archive] Failed to retrieve archived meals:', e);
      return [];
    }
  }

  /**
   * Restores all archived meals back to the main history.
   */
  public async restoreAllArchivedMeals(): Promise<void> {
    try {
      const archived = await this.getArchivedMeals();
      if (archived.length === 0) return;

      const savedHistory = await this.getItem('meal_history');
      let activeMeals: any[] = [];
      if (savedHistory) {
        try {
          activeMeals = JSON.parse(savedHistory);
        } catch (err) {
          console.warn('[LocalDB Archive] Failed to parse active meal history during restore:', err);
        }
      }

      // Merge archived meals into active history
      const activeMap = new Map<string, any>();
      activeMeals.forEach(m => {
        if (m.id) activeMap.set(m.id, m);
      });
      archived.forEach(m => {
        if (m.id) activeMap.set(m.id, m);
      });

      const mergedMeals = Array.from(activeMap.values());
      
      // Save back to active meal_history
      await this.setItem('meal_history', JSON.stringify(mergedMeals));
      
      // Clear the archive
      await this.removeItem('meal_history_archive');

      console.log(`[LocalDB Archive] Successfully restored ${archived.length} meals back to main history.`);
    } catch (e) {
      console.error('[LocalDB Archive] Failed to restore archived meals:', e);
      throw e;
    }
  }
}

/**
 * Compresses a string using Gzip/Deflate via Web CompressionStream API.
 * Falls back to plain string if browser doesn't support it or if it errors.
 */
export async function compressString(str: string): Promise<string> {
  if (typeof CompressionStream === 'undefined' || typeof Response === 'undefined' || typeof Blob === 'undefined') {
    return str;
  }
  try {
    const stream = new Blob([str]).stream();
    const compressedStream = stream.pipeThrough(new CompressionStream('gzip'));
    const response = new Response(compressedStream);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    
    // Convert arrayBuffer to base64 safely
    const uint8 = new Uint8Array(arrayBuffer);
    let binary = '';
    const len = uint8.byteLength;
    // Process in chunks to prevent stack overflow on large inputs
    const chunk = 0xffff;
    for (let i = 0; i < len; i += chunk) {
      const subArray = uint8.subarray(i, i + chunk);
      binary += String.fromCharCode.apply(null, subArray as any);
    }
    return btoa(binary);
  } catch (e) {
    console.warn('[Compression] Failed to compress, falling back to raw:', e);
    return str;
  }
}

/**
 * Decompresses a base64 gzipped string.
 * Automatically detects if the input is uncompressed (JSON) and returns as is.
 */
export async function decompressString(compressed: string): Promise<string> {
  if (!compressed) return '';
  // Check if it's already uncompressed JSON
  const trimmed = compressed.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return compressed;
  }
  if (typeof DecompressionStream === 'undefined' || typeof Response === 'undefined') {
    return compressed;
  }
  try {
    const binary = atob(compressed);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const stream = new Blob([bytes]).stream();
    const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'));
    const response = new Response(decompressedStream);
    return await response.text();
  } catch (e) {
    console.warn('[Compression] Failed to decompress, returning original string:', e);
    return compressed;
  }
}

export const localDb = new LocalDbService();
